/**
 * Iter 47 · Captação e Atualização de Base (Analista MKT)
 * Upload de Excel com municípios+pessoas. Sistema roteia automaticamente:
 * - Município já em carteira → cria pessoa direto, notifica vendedora + diretor
 * - Sem vendedora ou novo → vira lead pendente para o Diretor direcionar
 */
import { useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Download, Database, CheckCircle2, AlertCircle, FileSpreadsheet, ChevronRight } from "lucide-react";

export default function CaptacaoBase() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resultado, setResultado] = useState(null);

  const baixarTemplate = () => {
    const token = localStorage.getItem("ceap_token");
    // Faz download via fetch para incluir header Authorization
    fetch(`${BACKEND_URL}/api/captacao/template.xlsx`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "captacao_template.xlsx";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Erro ao baixar template"));
  };

  const enviar = async () => {
    if (!file) { toast.error("Selecione um arquivo"); return; }
    setBusy(true);
    setResultado(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/captacao/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResultado(r.data);
      const total = r.data.linhas_processadas;
      toast.success(`✅ ${total} linha${total > 1 ? "s" : ""} processada${total > 1 ? "s" : ""}!`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : (detail || "Erro no upload"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="captacao-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
          <Database className="w-8 h-8 text-purple-600" />
          Captação e Atualização de Base
        </h1>
        <p className="text-slate-500 text-sm">
          Suba planilhas de novos leads/pessoas. O sistema identifica se o município já está em uma carteira e roteia sozinho.
        </p>
      </div>

      {/* Como funciona */}
      <Card className="p-5 rounded-2xl border-2 border-purple-200 bg-purple-50/40">
        <div className="text-xs uppercase tracking-widest font-bold text-purple-700 mb-3">Como funciona</div>
        <ol className="space-y-2 text-sm text-slate-700">
          <li className="flex gap-2"><span className="font-bold text-purple-600 shrink-0">1.</span> Baixe o template Excel com o cabeçalho oficial (municipio, uf, tipo_orgao, cnpj, pessoa_nome, whatsapp…)</li>
          <li className="flex gap-2"><span className="font-bold text-purple-600 shrink-0">2.</span> Preencha 1 linha por PESSOA. Repita a mesma dupla municipio+uf para adicionar várias pessoas ao mesmo órgão.</li>
          <li className="flex gap-2"><span className="font-bold text-purple-600 shrink-0">3.</span> Faça o upload. O sistema decide:
            <div className="ml-4 mt-1 space-y-1">
              <div className="flex gap-1.5 items-start text-xs"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" /> Município já está numa carteira ativa? A pessoa entra direto lá. A vendedora e o Diretor recebem notificação.</div>
              <div className="flex gap-1.5 items-start text-xs"><AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" /> Município sem vendedora ou novo? Vira "Lead Pendente" e o Diretor direciona em /leads-pendentes.</div>
            </div>
          </li>
        </ol>
        <Button onClick={baixarTemplate} variant="outline" className="mt-4 border-purple-300 text-purple-700 hover:bg-purple-100" data-testid="btn-baixar-template">
          <Download className="w-4 h-4 mr-1" /> Baixar template Excel
        </Button>
      </Card>

      {/* Upload */}
      <Card className="p-6 rounded-2xl space-y-4">
        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 flex items-center gap-2">
          <Upload className="w-4 h-4" /> Enviar planilha
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-slate-600">Arquivo Excel (.xlsx) *</label>
          <Input
            type="file"
            accept=".xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="input-file-captacao"
          />
          {file && (
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <FileSpreadsheet className="w-3 h-3" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>
        <Button
          onClick={enviar}
          disabled={busy || !file}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="btn-enviar-captacao"
        >
          <Upload className="w-4 h-4 mr-1" /> {busy ? "Processando..." : "Enviar planilha"}
        </Button>
      </Card>

      {/* Resultado */}
      {resultado && (
        <Card className="p-5 rounded-2xl border-2 border-emerald-200 bg-emerald-50/40" data-testid="resultado-captacao">
          <div className="text-xs uppercase tracking-widest font-bold text-emerald-700 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Processado com sucesso
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl p-3 border border-slate-200">
              <div className="text-[11px] uppercase text-slate-500 font-bold">Linhas processadas</div>
              <div className="text-2xl font-display font-bold text-slate-900 tabular-nums">{resultado.linhas_processadas}</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-emerald-200">
              <div className="text-[11px] uppercase text-emerald-700 font-bold">Direto na carteira</div>
              <div className="text-2xl font-display font-bold text-emerald-700 tabular-nums">{resultado.pessoas_criadas_direto}</div>
              <div className="text-[10px] text-slate-500">Vendedora notificada</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-amber-200">
              <div className="text-[11px] uppercase text-amber-700 font-bold">Leads pendentes</div>
              <div className="text-2xl font-display font-bold text-amber-700 tabular-nums">{resultado.leads_pendentes_criados}</div>
              <div className="text-[10px] text-slate-500">Aguardando Diretor</div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-slate-200">
              <div className="text-[11px] uppercase text-slate-500 font-bold">Vendedoras notificadas</div>
              <div className="text-2xl font-display font-bold text-slate-900 tabular-nums">{resultado.vendedoras_notificadas}</div>
            </div>
          </div>
          {resultado.erros?.length > 0 && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3">
              <div className="text-xs font-bold uppercase text-red-700 mb-1 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {resultado.erros.length} linha{resultado.erros.length > 1 ? "s" : ""} com erro
              </div>
              <ul className="text-xs text-red-700 space-y-0.5">
                {resultado.erros.slice(0, 10).map((e, i) => (
                  <li key={i} className="flex gap-1"><ChevronRight className="w-3 h-3 shrink-0 mt-0.5" /> {e}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
